import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';
const STAGES = {
  enter: 0,
  thinking: 600,
  handoff: 1500
} as const;
export function AiHandoff({
  prompt,
  onComplete
}: {
  prompt: string;
  onComplete: () => void;
}) {
  const [stage, setStage] = useState<'enter' | 'thinking'>('enter');
  useEffect(() => {
    const t1 = setTimeout(() => setStage('thinking'), STAGES.thinking);
    const t2 = setTimeout(onComplete, STAGES.handoff);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [onComplete]);
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    duration: 0.25
  }} className="fixed inset-0 z-[100] overflow-hidden bg-surface-100" data-id="element-113">
      {/* Soft brand-tinted radial glow — matches Home's hero glow */}
      <div className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[1100px] h-[700px] bg-gradient-to-b from-brand-50/90 via-brand-50/40 to-transparent rounded-full blur-3xl" data-id="element-114" />
      <motion.div animate={{
      opacity: [0.5, 0.8, 0.5]
    }} transition={{
      duration: 2.5,
      repeat: Infinity,
      ease: 'easeInOut'
    }} className="pointer-events-none absolute -top-32 right-[10%] w-[400px] h-[400px] bg-gradient-to-br from-brand-100/60 to-transparent rounded-full blur-3xl" data-id="element-115" />
      <motion.div animate={{
      opacity: [0.4, 0.7, 0.4]
    }} transition={{
      duration: 3,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: 0.5
    }} className="pointer-events-none absolute bottom-0 left-[10%] w-[450px] h-[450px] bg-gradient-to-tr from-amber-50/70 to-transparent rounded-full blur-3xl" data-id="element-116" />

      {/* Subtle dot pattern */}
      <div className="absolute inset-0 opacity-[0.04]" style={{
      backgroundImage: 'radial-gradient(rgba(15,23,42,1) 1px, transparent 1px)',
      backgroundSize: '32px 32px'
    }} data-id="element-117" />

      {/* Floating particles — warm brand tone */}
      {[...Array(16)].map((_, i) => <motion.div key={i} className="absolute w-1 h-1 rounded-full bg-brand-400/40" initial={{
      x: `${Math.random() * 100}%`,
      y: '110%',
      opacity: 0
    }} animate={{
      y: '-10%',
      opacity: [0, 1, 0]
    }} transition={{
      duration: 2.5 + Math.random() * 1.5,
      repeat: Infinity,
      delay: Math.random() * 2,
      ease: 'linear'
    }} style={{
      left: `${Math.random() * 100}%`
    }} data-id="element-118" />)}

      {/* Soft vertical light beam */}
      <motion.div initial={{
      opacity: 0,
      scaleY: 0
    }} animate={{
      opacity: [0, 0.35, 0],
      scaleY: [0, 1, 0]
    }} transition={{
      duration: 1.2,
      ease: 'easeOut'
    }} className="absolute left-1/2 top-0 -translate-x-1/2 w-px h-full bg-gradient-to-b from-transparent via-brand-300/60 to-transparent origin-top" data-id="element-119" />

      {/* Main content */}
      <div className="relative z-10 h-full flex flex-col items-center justify-center px-6" data-id="element-120">
        {/* User prompt — matches Home's search bar aesthetic */}
        <motion.div initial={{
        y: 60,
        opacity: 0,
        scale: 0.95
      }} animate={{
        y: 0,
        opacity: 1,
        scale: 1
      }} transition={{
        type: 'spring',
        stiffness: 320,
        damping: 26,
        delay: 0.05
      }} className="mb-12 max-w-2xl w-full" data-id="element-121">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-400 mb-3 text-center flex items-center justify-center gap-2" data-id="element-122">
            <span className="w-6 h-px bg-gray-300" data-id="element-123" />
            Your request
            <span className="w-6 h-px bg-gray-300" data-id="element-124" />
          </div>
          <div className="relative group" data-id="element-125">
            <div className="absolute -inset-1 bg-gradient-to-r from-brand-400 to-brand-600 rounded-[2rem] blur opacity-20" data-id="element-126" />
            <div className="relative bg-white rounded-[2rem] shadow-xl border border-gray-100 px-7 py-5 text-gray-900 text-lg sm:text-xl font-medium leading-snug text-center" data-id="element-127">
              "{prompt}"
            </div>
          </div>
        </motion.div>

        {/* AI Constellation — warm brand orbs */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.5
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.3,
        duration: 0.4,
        ease: 'easeOut'
      }} className="relative w-40 h-40 flex items-center justify-center" data-id="element-128">
          {/* Rotating orbital ring */}
          <motion.div animate={{
          rotate: 360
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'linear'
        }} className="absolute inset-0" data-id="element-129">
            {[0, 120, 240].map((angle, i) => <motion.div key={angle} className="absolute top-1/2 left-1/2 w-3 h-3 -ml-1.5 -mt-1.5" style={{
            transform: `rotate(${angle}deg) translateX(72px)`
          }} data-id="element-130">
                <motion.div animate={{
              scale: [1, 1.6, 1],
              opacity: [0.6, 1, 0.6]
            }} transition={{
              duration: 1.2,
              repeat: Infinity,
              delay: i * 0.4,
              ease: 'easeInOut'
            }} className={`w-3 h-3 rounded-full ${i === 0 ? 'bg-brand-500' : i === 1 ? 'bg-brand-400' : 'bg-amber-400'} shadow-[0_0_16px_currentColor]`} data-id="element-131" />
              </motion.div>)}
          </motion.div>

          {/* Soft pulse rings */}
          <motion.div animate={{
          scale: [1, 1.4, 1],
          opacity: [0.5, 0, 0.5]
        }} transition={{
          duration: 1.5,
          repeat: Infinity,
          ease: 'easeOut'
        }} className="absolute w-24 h-24 rounded-full border border-brand-300/60" data-id="element-132" />
          <motion.div animate={{
          scale: [1, 1.7, 1],
          opacity: [0.35, 0, 0.35]
        }} transition={{
          duration: 1.5,
          repeat: Infinity,
          ease: 'easeOut',
          delay: 0.4
        }} className="absolute w-24 h-24 rounded-full border border-brand-200/60" data-id="element-133" />

          {/* Central core — brand gradient with hero-glow style */}
          <motion.div animate={{
          scale: [1, 1.05, 1],
          boxShadow: ['0 0 30px rgba(255,107,53,0.35), 0 12px 40px rgba(255,107,53,0.25)', '0 0 50px rgba(255,107,53,0.5), 0 12px 60px rgba(255,107,53,0.35)', '0 0 30px rgba(255,107,53,0.35), 0 12px 40px rgba(255,107,53,0.25)']
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="relative w-20 h-20 rounded-3xl orange-gradient-bg flex items-center justify-center" data-id="element-134">
            <Sparkles className="w-9 h-9 text-white" data-id="element-135" />
          </motion.div>
        </motion.div>

        {/* Status copy — light theme */}
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.45
      }} className="mt-14 text-center" data-id="element-136">
          <div className="inline-flex items-center gap-2 bg-white border border-gray-200 px-3 py-1 rounded-full shadow-sm mb-4" data-id="element-137">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" data-id="element-138" />
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-600" data-id="element-139">
              Pair2 AI · Live
            </span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-display font-bold text-gray-900 mb-3" data-id="element-140">
            {stage === 'enter' ? <>
                Analyzing your{' '}
                <span className="orange-gradient-text" data-id="element-141">request</span>
              </> : <>
                Matching you with{' '}
                <span className="orange-gradient-text" data-id="element-142">the best carriers</span>
              </>}
          </h2>
          <div className="flex justify-center gap-1.5" data-id="element-143">
            {[0, 1, 2].map(i => <motion.span key={i} animate={{
            opacity: [0.3, 1, 0.3],
            y: [0, -3, 0]
          }} transition={{
            duration: 0.9,
            repeat: Infinity,
            delay: i * 0.15,
            ease: 'easeInOut'
          }} className="w-1.5 h-1.5 rounded-full bg-brand-500" data-id="element-144" />)}
          </div>
        </motion.div>
      </div>
    </motion.div>;
}